import {
	BagPaymentBankAccount,
	BagPaymentListAddModalData,
	BagPaymentPositionFormValue,
} from '@/bag/bag-payment-list/bag-payment-list.model';
import { BagPaymentListAddPositionModalComponent } from '@/bag/bag-payment-list/bag-payment-list-add/bag-payment-list-add-modal/bag-payment-list-add-position-modal/bag-payment-list-add-position-modal.component';
import { BagPaymentListService } from '@/bag/bag-payment-list/bag-payment-list.service';
import { DataBagService } from '@/common/data-bag.service';
import { DateService } from '@/common/date.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import { UserData } from '@/reservation/reservation.model';
import { Component, DestroyRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { debounceTime, map, startWith } from 'rxjs';

@Component({
	selector: 'app-bag-payment-list-add-modal',
	templateUrl: './bag-payment-list-add-modal.component.html',
	styleUrls: ['./bag-payment-list-add-modal.component.scss'],
})
export class BagPaymentListAddModalComponent implements OnInit, OnDestroy {
	@ViewChild('dataTable') dataTable: MatTable<FormGroup>;

	private readonly dialogRef = inject(MatDialogRef<BagPaymentListAddModalComponent>);
	readonly dialogData = inject<BagPaymentListAddModalData>(MAT_DIALOG_DATA);
	private readonly bag = inject(DataBagService);
	private readonly loader = inject(LoaderService);
	private readonly infobox = inject(InfoboxService);
	private readonly fb = inject(FormBuilder);
	private readonly dialog = inject(MatDialog);
	private readonly bagPaymentListService = inject(BagPaymentListService);
	private readonly date = inject(DateService);
	private readonly destroyRef = inject(DestroyRef);

	submitted = false;
	filteredBankAccountPosting: BagPaymentBankAccount[] = [];
	readonly showColumns = [
		'tableId',
		'userData',
		'amount',
		'payment',
		'bankAccountIncoming',
		'invoiceNumber',
		'note',
		'actions',
	];

	readonly form = this.fb.group({
		bankAccountPosting: ['', Validators.required],
		posting: ['', Validators.required],
		payments: this.fb.array<FormGroup>([]),
	});

	get payments(): FormArray<FormGroup> {
		return this.form.controls.payments;
	}

	ngOnInit(): void {
		this.form.controls.bankAccountPosting.valueChanges
			.pipe(
				startWith(''),
				debounceTime(400),
				map((value) => this.filterAccount(value)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((filtered) => {
				this.filteredBankAccountPosting = filtered;
			});

		this.bagPaymentListService.users.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((users) => {
			this.dialogData.profileData = users;
		});
	}

	ngOnDestroy(): void {
		this.bagPaymentListService.users.unsubscribe();
	}

	addPos(): void {
		this.openPositionDialog();
	}

	editPos(paymentIndex: number): void {
		this.openPositionDialog(paymentIndex);
	}

	displayFn(item: string | number | null): string {
		const account = this.dialogData.bankAccounts.find((bankAccount) => bankAccount.id === item);
		if (!item || !account) {
			return '';
		}

		return this.getBankAccountOptionLabel(account);
	}

	getBankAccountOptionLabel(account: BagPaymentBankAccount): string {
		const organizationName =
			typeof account.organization === 'object' ? account.organization?.shortName : account.shortName;
		return `${organizationName ?? ''} ${account.number}`.trim();
	}

	getUserLabel(userId: number | string | null | undefined): string {
		const profile = this.dialogData.profileData.find((profileItem) => profileItem.id === userId);
		if (!profile) {
			return '';
		}

		return `${profile.firstName} ${profile.lastName}`;
	}

	selectOption($event: MatAutocompleteSelectedEvent): void {
		return $event.option.value;
	}

	save(): void {
		this.submitted = true;
		if (!this.form.valid) {
			this.infobox.error('Uzupełnij wymagane pola');
			return;
		}

		if (!this.payments.length) {
			return;
		}

		const data = this.payments.controls.map((control) => {
			const value = { ...control.getRawValue() };
			value.bankAccountPosting = this.form.value.bankAccountPosting;
			value.posting = this.date.apiToDateTime(this.form.value.posting).date;
			value.payment = this.date.apiToDateTime(value.payment).date;
			value.amount = this.apiToAmount(value.amount);
			return value;
		});

		this.send(data);
	}

	onNoClick(): void {
		this.dialogRef.close();
	}

	removeRow(paymentIndex: number): void {
		this.payments.removeAt(paymentIndex);
		this.dataTable.renderRows();
	}

	private openPositionDialog(paymentIndex?: number): void {
		const position =
			paymentIndex !== undefined
				? (this.payments.at(paymentIndex).getRawValue() as BagPaymentPositionFormValue)
				: undefined;

		this.dialog
			.open(BagPaymentListAddPositionModalComponent, {
				width: '900px',
				maxWidth: '95vw',
				data: {
					profileData: this.dialogData.profileData,
					position,
				},
			})
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result?: BagPaymentPositionFormValue) => {
				if (!result) {
					return;
				}

				if (paymentIndex !== undefined) {
					this.payments.at(paymentIndex).patchValue(result);
				} else {
					this.payments.push(this.createPaymentRow(result));
				}

				this.dataTable.renderRows();
			});
	}

	private createPaymentRow(position: BagPaymentPositionFormValue): FormGroup {
		return this.fb.group({
			tableId: new FormControl(position.tableId, [Validators.required, Validators.minLength(3)]),
			userData: new FormControl(position.userData, [Validators.required]),
			amount: new FormControl(position.amount, [Validators.required]),
			kind: position.kind,
			organization: position.organization,
			bankAccountIncoming: new FormControl(position.bankAccountIncoming, [Validators.required]),
			invoiceNumber: position.invoiceNumber,
			status: position.status,
			currency: position.currency,
			payment: new FormControl(position.payment, [Validators.required]),
			posting: position.posting,
			table_name: position.table_name,
			note: position.note ?? '',
		});
	}

	private filterAccount(value: unknown): BagPaymentBankAccount[] {
		if (value && typeof value === 'string') {
			const filterValue = value.toLowerCase();

			if (filterValue.length < 3) {
				return this.dialogData.bankAccounts;
			}

			return this.dialogData.bankAccounts.filter((option) => {
				const organizationName =
					typeof option.organization === 'object' ? option.organization?.shortName : option.shortName;
				return (
					organizationName?.toLowerCase()?.includes(filterValue) ||
					option.number?.toLowerCase()?.includes(filterValue)
				);
			});
		}

		return this.dialogData.bankAccounts;
	}

	private apiToAmount(value: number): string {
		return (value * 100).toFixed(0);
	}

	private send(data: unknown[]): void {
		this.bag.post(`/payment/`, data, (result, error) => {
			if (error) {
				this.loader.hide();
				return;
			}

			this.dialogRef.close('send');
		});
	}
}
